/**
 * PROJ-005: RBAC Security System
 * Hooks for checking user permissions and access levels in the frontend
 *
 * @module hooks/usePermission
 * @see docs/SECURITY-RBAC-API-SPECIFICATION.md - Complete API documentation
 * @see proyectos/PROJ-005-RBAC-Security-System/README.md - Project overview
 */

import { useEffect, useState, useMemo } from 'react';
import { getCachedUser, getAccessToken } from '@/lib/auth';

// ============================================================================
// Cache Utilities
// ============================================================================

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MATRIX_TTL = 5 * 60 * 1000; // 5 minutes for full permission matrix

/**
 * Get cached permission from sessionStorage
 */
function getCachedPermission(key: string): boolean | null {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;

    const { value, expiry } = JSON.parse(cached);
    if (Date.now() > expiry) {
      sessionStorage.removeItem(key);
      return null;
    }
    return value;
  } catch (error) {
    console.error('[Cache] Error reading cache:', error);
    return null;
  }
}

/**
 * Set cached permission in sessionStorage
 */
function setCachedPermission(key: string, value: boolean): void {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      value,
      expiry: Date.now() + CACHE_TTL
    }));
  } catch (error) {
    console.error('[Cache] Error writing cache:', error);
  }
}

/**
 * Get cached access level from sessionStorage
 */
function getCachedAccessLevel(key: string): AccessLevelString | null {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;

    const { value, expiry } = JSON.parse(cached);
    if (Date.now() > expiry) {
      sessionStorage.removeItem(key);
      return null;
    }
    return value;
  } catch (error) {
    console.error('[Cache] Error reading cache:', error);
    return null;
  }
}

/**
 * Set cached access level in sessionStorage
 */
function setCachedAccessLevel(key: string, value: AccessLevelString): void {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      value,
      expiry: Date.now() + CACHE_TTL
    }));
  } catch (error) {
    console.error('[Cache] Error writing cache:', error);
  }
}

// ============================================================================
// Permission Matrix Hydration
// ============================================================================

type PermissionMatrixEntry = {
  allowed: boolean;
  accessLevel: AccessLevelString;
};

type PermissionMatrix = {
  userId: string;
  expiresAt: number;
  map: Record<string, PermissionMatrixEntry>;
};

let inMemoryMatrix: PermissionMatrix | null = null;
let matrixPromise: Promise<PermissionMatrix | null> | null = null;

function matrixCacheKey(userId: string) {
  return `perm-matrix:${userId}`;
}

function buildMatrix(userId: string, privileges: Array<{ entity: string; action: string; accessLevel?: AccessLevelString }>): PermissionMatrix {
  const map: Record<string, PermissionMatrixEntry> = {};

  privileges.forEach((p) => {
    const key = `${p.entity}:${p.action}`;
    const level = (p.accessLevel || 'NONE') as AccessLevelString;
    map[key] = {
      allowed: level !== 'NONE',
      accessLevel: level,
    };
  });

  return {
    userId,
    expiresAt: Date.now() + MATRIX_TTL,
    map,
  };
}

function loadMatrixFromStorage(userId: string): PermissionMatrix | null {
  try {
    const raw = sessionStorage.getItem(matrixCacheKey(userId));
    if (!raw) return null;
    const parsed: PermissionMatrix = JSON.parse(raw);
    if (parsed.userId !== userId) return null;
    if (Date.now() > parsed.expiresAt) {
      sessionStorage.removeItem(matrixCacheKey(userId));
      return null;
    }
    return parsed;
  } catch (error) {
    console.error('[PermissionMatrix] Failed to read cache:', error);
    return null;
  }
}

function saveMatrixToStorage(matrix: PermissionMatrix) {
  try {
    sessionStorage.setItem(matrixCacheKey(matrix.userId), JSON.stringify(matrix));
  } catch (error) {
    console.error('[PermissionMatrix] Failed to persist cache:', error);
  }
}

function setInMemoryMatrix(matrix: PermissionMatrix | null) {
  inMemoryMatrix = matrix;
  if (!matrix) return;
  saveMatrixToStorage(matrix);
}

function getMatrixEntry(userId: string | undefined, entity: string, privilege: string): PermissionMatrixEntry | null {
  if (!userId) return null;

  const now = Date.now();
  if (inMemoryMatrix && inMemoryMatrix.userId === userId && inMemoryMatrix.expiresAt > now) {
    return inMemoryMatrix.map[`${entity}:${privilege}`] || null;
  }

  const stored = loadMatrixFromStorage(userId);
  if (stored && stored.expiresAt > now) {
    inMemoryMatrix = stored;
    return stored.map[`${entity}:${privilege}`] || null;
  }

  return null;
}

async function hydratePermissionMatrix(userId: string): Promise<PermissionMatrix | null> {
  const now = Date.now();

  if (inMemoryMatrix && inMemoryMatrix.userId === userId && inMemoryMatrix.expiresAt > now) {
    return inMemoryMatrix;
  }

  const stored = loadMatrixFromStorage(userId);
  if (stored && stored.expiresAt > now) {
    inMemoryMatrix = stored;
    return stored;
  }

  if (matrixPromise) return matrixPromise;

  matrixPromise = (async () => {
    try {
      const token = getAccessToken();
      const response = await fetch('/api/v1/security/my-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        console.warn(`[PermissionMatrix] API error: ${response.status}`);
        setInMemoryMatrix(null);
        return null;
      }

      const result = await response.json();
      const privileges = result?.data?.privileges || [];

      const matrix = buildMatrix(userId, privileges);
      setInMemoryMatrix(matrix);
      return matrix;
    } catch (error) {
      console.error('[PermissionMatrix] Failed to hydrate:', error);
      setInMemoryMatrix(null);
      return null;
    } finally {
      matrixPromise = null;
    }
  })();

  return matrixPromise;
}

export function clearPermissionMatrix(userId?: string) {
  if (userId) {
    sessionStorage.removeItem(matrixCacheKey(userId));
  }
  inMemoryMatrix = null;
  matrixPromise = null;
}

/**
 * Access levels for RBAC system (aligned with backend)
 * Higher levels include all permissions of lower levels
 */
export enum AccessLevel {
  NONE = 0,
  USER = 1,
  TEAM = 2,
  BUSINESS_UNIT = 3,
  PARENT_CHILD_BU = 4,
  ORGANIZATION = 5,
}

/**
 * Access level string type (for API compatibility)
 */
export type AccessLevelString =
  | 'NONE'
  | 'USER'
  | 'TEAM'
  | 'BUSINESS_UNIT'
  | 'PARENT_CHILD_BU'
  | 'ORGANIZATION';

/**
 * Privilege types (34 total - aligned with PROJ-005 specification + PROJ-047 + PROJ-040 Wave 21/22 + PROJ-058)
 */
export type Privilege =
  | 'CREATE'
  | 'READ'
  | 'UPDATE'
  | 'DELETE'
  | 'BULK_DELETE'
  | 'ARCHIVE'
  | 'SHARE'
  | 'ASSIGN'
  | 'APPEND'
  | 'APPEND_TO'
  | 'EXPORT'
  | 'IMPORT'
  | 'PUBLISH'
  | 'APPROVE'
  | 'CANCEL'
  | 'CONFIGURE'
  | 'ADMINISTER_SECURITY'
  | 'VIEW_AUDIT'
  // PROJ-047: Entity Comparison View
  | 'COMPARE'
  | 'MERGE'
  | 'VIEW_HISTORY'
  | 'ROLLBACK'
  // PROJ-051: Network Diagnostics
  | 'EXECUTE'
  // PROJ-054: Workflow Automation Engine
  | 'MANAGE_SECRETS'
  // PROJ-040: React Email System
  | 'SEND'
  | 'SEND_TEST'
  | 'VIEW_ANALYTICS'
  // PROJ-040 Wave 21/22: Email Template Format Access
  | 'ACCESS_VISUAL'
  | 'ACCESS_MJML'
  | 'ACCESS_HTML'
  | 'CONVERT_FORMAT'
  // PROJ-058: Documents Module
  | 'MOVE'
  | 'RESTORE'
  | 'DOWNLOAD'
  | 'UNSHARE'
  | 'CHECK_OUT'
  | 'CHECK_IN'
  // PROJ-082: Privacy Governance Canvas Integration
  | 'ASSESS';

/**
 * Entity types in the system (25+ entities)
 */
export type Entity =
  | 'users'
  | 'visual_flows'
  | 'workflow_automation_workflows'
  | 'workflow_runs'
  | 'workflow_secrets'
  | 'workflow_nodes'
  | 'workflow_edges'
  | 'incidents'
  | 'persons'
  | 'companies'
  | 'contacts'
  | 'teams'
  | 'business_units'
  | 'departments'
  | 'divisions'
  | 'tags'
  | 'industries'
  | 'brands'
  | 'products'
  | 'documents'
  | 'audits'
  | 'settings'
  | 'locations'
  | 'office_locations'
  | 'security_roles'
  | 'audit_logs'
  | 'notes'
  | 'webhooks'
  | 'sla_policies'
  | 'compliance_laws'
  | 'cost_centers'
  | 'organization_units'
  | 'employee_types'
  | 'job_positions'
  | 'user_statuses'
  | 'data_classifications'  // PROJ-042 Phase 1
  | 'data_attributes'       // PROJ-042 Phase 2
  | 'security'
  | 'network_diagnostics'  // PROJ-051
  | 'webhook_settings'  // PROJ-041 Wave 7
  | 'system_api_keys' // RBAC entity for API Keys management
  // PROJ-040: React Email System
  | 'email_templates'
  | 'email_queue'
  | 'email_assets'
  | 'email_branding'
  | 'email_send'
  | 'email_providers' // PROJ-040: Email Provider Configuration CRUD
  | 'email_dictionary' // Wave 22: API-style email dictionary access
  | 'email_dead_letter_queue' // Wave 22: API-style email DLQ access
  | 'merge_history' // Wave 22: API-style merge history access
  | 'search' // Wave 22: API-style search access
  | 'mcp_tools' // Wave 22: MCP tool execution access
  | 'field_metadata' // PROJ-057: Entity Field Metadata System
  // PROJ-058: Documents Module
  | 'document_folders'
  | 'document_files'
  | 'document_categories'
  // PROJ-054: Legacy alias
  | 'workflows'
  // PROJ-080: Credentials Management System
  | 'credentials'
  | 'credential_types'
  // PROJ-086: Artifacts Module
  | 'artifacts'
  // PROJ-082: Privacy Governance Canvas Integration
  | 'privacy_governance';

/**
 * Hook to check if current user has permission for an entity/privilege
 *
 * Calls the backend RBAC API to check permissions with sessionStorage caching.
 * Cache TTL is 5 minutes to balance performance with permission changes.
 *
 * @param entity - The entity to check permission for (e.g., 'incidents', 'users')
 * @param privilege - The privilege to check (e.g., 'CREATE', 'ARCHIVE', 'DELETE')
 * @returns boolean - true if user has permission, false otherwise
 *
 * @example
 * ```tsx
 * function IncidentActions({ incident }) {
 *   const canArchive = usePermission('incidents', 'ARCHIVE');
 *   const canDelete = usePermission('incidents', 'DELETE');
 *
 *   return (
 *     <div className="flex gap-2">
 *       {canArchive && <ArchiveButton onClick={() => handleArchive(incident.id)} />}
 *       {canDelete && <DeleteButton onClick={() => handleDelete(incident.id)} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermission(entity: Entity, privilege: Privilege): boolean {
  const user = getCachedUser();
  // Use stable user ID reference to prevent infinite re-renders
  const userId = user?.id;
  
  // Initialize state from cache to prevent flashing
  const [hasPermission, setHasPermission] = useState<boolean>(() => {
    if (!userId) return false;
    const matrixEntry = getMatrixEntry(userId, entity, privilege);
    if (matrixEntry) return matrixEntry.allowed;
    const cacheKey = `perm:${userId}:${entity}:${privilege}`;
    const cached = getCachedPermission(cacheKey);
    return cached !== null ? cached : false;
  });

  useEffect(() => {
    // If no user is authenticated, deny permission
    if (!userId) {
      setHasPermission(false);
      return;
    }

    const cacheKey = `perm:${userId}:${entity}:${privilege}`;

    let cancelled = false;

    (async () => {
      // Step 1: try to hydrate full matrix to avoid per-permission fetch
      const matrix = await hydratePermissionMatrix(userId);
      if (cancelled) return;

      const matrixEntry = getMatrixEntry(userId, entity, privilege);
      if (matrixEntry) {
        setCachedPermission(cacheKey, matrixEntry.allowed);
        setHasPermission(matrixEntry.allowed);
        return;
      }

      // Step 2: fallback to single permission check if matrix missing entry
      const cached = getCachedPermission(cacheKey);
      if (cached !== null) {
        setHasPermission(cached);
        return;
      }

      try {
        const token = getAccessToken();
        const response = await fetch(`/api/v1/security/check-permission?entity=${entity}&action=${privilege}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include'
        });

        if (cancelled) return;

        if (!response.ok) {
          console.warn(`[usePermission] API error: ${response.status}`);
          setHasPermission(false);
          return;
        }

        const result = await response.json();
        const allowed = result?.data?.allowed || false;

        if (cancelled) return;

        // Cache the result
        setCachedPermission(cacheKey, allowed);
        setHasPermission(allowed);

      } catch (error) {
        if (cancelled) return;
        console.error('[usePermission] Error checking permission:', error);
        setHasPermission(false);
      }
    })();

    // Cleanup function to prevent state updates after unmount
    return () => {
      cancelled = true;
    };
  }, [userId, entity, privilege]);

  return hasPermission;
}

/**
 * Hook to get access level for an entity/privilege
 *
 * Returns the access level granted to the current user for a specific entity and privilege.
 * Access levels determine the scope of records the user can access:
 * - NONE: No access
 * - USER: Only records owned by the user
 * - TEAM: Records owned by user's team members
 * - BUSINESS_UNIT: Records in user's business unit
 * - PARENT_CHILD_BU: Records in user's BU and child BUs
 * - ORGANIZATION: All records in the organization
 *
 * @param entity - The entity type
 * @param privilege - The privilege to check
 * @returns AccessLevelString - The access level granted ('NONE' if no permission)
 *
 * @example
 * ```tsx
 * function WorkflowEditor() {
 *   const updateLevel = useAccessLevel('visual_flows', 'UPDATE');
 *
 *   if (updateLevel === 'USER') {
 *     // Can only update own workflows
 *   } else if (updateLevel === 'BUSINESS_UNIT') {
 *     // Can update all workflows in business unit
 *   }
 * }
 * ```
 */
export function useAccessLevel(entity: Entity, privilege: Privilege): AccessLevelString {
  const user = getCachedUser();
  // Use stable user ID reference to prevent infinite re-renders
  const userId = user?.id;
  
  // Initialize state from cache
  const [accessLevel, setAccessLevel] = useState<AccessLevelString>(() => {
    if (!userId) return 'NONE';
    const matrixEntry = getMatrixEntry(userId, entity, privilege);
    if (matrixEntry) return matrixEntry.accessLevel;
    const cacheKey = `access:${userId}:${entity}:${privilege}`;
    const cached = getCachedAccessLevel(cacheKey);
    return cached !== null ? cached : 'NONE';
  });

  useEffect(() => {
    // If no user is authenticated, return NONE
    if (!userId) {
      setAccessLevel('NONE');
      return;
    }

    const cacheKey = `access:${userId}:${entity}:${privilege}`;

    let cancelled = false;

    (async () => {
      // Step 1: hydrate full matrix first
      await hydratePermissionMatrix(userId);
      if (cancelled) return;

      const matrixEntry = getMatrixEntry(userId, entity, privilege);
      if (matrixEntry) {
        setCachedAccessLevel(cacheKey, matrixEntry.accessLevel);
        setAccessLevel(matrixEntry.accessLevel);
        return;
      }

      const cached = getCachedAccessLevel(cacheKey);
      if (cached !== null) {
        setAccessLevel(cached);
        return;
      }

      try {
        const token = getAccessToken();
        const response = await fetch(`/api/v1/security/check-permission?entity=${entity}&action=${privilege}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include'
        });

        if (cancelled) return;

        if (!response.ok) {
          console.warn(`[useAccessLevel] API error: ${response.status}`);
          setAccessLevel('NONE');
          return;
        }

        const result = await response.json();
        // TODO: Backend will include accessLevel in response once entity_permissions is implemented
        // For now, derive from 'allowed' boolean
        const level: AccessLevelString = result?.data?.allowed ? 'ORGANIZATION' : 'NONE';

        if (cancelled) return;

        // Cache the result
        setCachedAccessLevel(cacheKey, level);
        setAccessLevel(level);

      } catch (error) {
        if (cancelled) return;
        console.error('[useAccessLevel] Error checking access level:', error);
        setAccessLevel('NONE');
      }
    })();

    // Cleanup function to prevent state updates after unmount
    return () => {
      cancelled = true;
    };
  }, [userId, entity, privilege]);

  return accessLevel;
}

/**
 * Hook to check multiple permissions at once
 *
 * Useful for checking several permissions in a single operation, avoiding multiple re-renders.
 * More efficient than calling usePermission multiple times.
 *
 * @param checks - Array of permission checks to perform
 * @returns Object mapping "entity:privilege" to boolean
 *
 * @example
 * ```tsx
 * function IncidentEditor() {
 *   const permissions = usePermissions([
 *     { entity: 'incidents', privilege: 'UPDATE' },
 *     { entity: 'incidents', privilege: 'DELETE' },
 *     { entity: 'incidents', privilege: 'ARCHIVE' },
 *     { entity: 'visual_flows', privilege: 'CREATE' },
 *   ]);
 *
 *   return (
 *     <div>
 *       {permissions['incidents:UPDATE'] && <EditButton />}
 *       {permissions['incidents:DELETE'] && <DeleteButton />}
 *       {permissions['incidents:ARCHIVE'] && <ArchiveButton />}
 *       {permissions['workflows:CREATE'] && <CreateWorkflowButton />}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePermissions(
  checks: Array<{ entity: Entity; privilege: Privilege }>
): Record<string, boolean> {
  const user = getCachedUser();
  // Use stable user ID reference to prevent infinite re-renders
  const userId = user?.id;
  // Stabilize checks array by creating a string key
  const checksKey = useMemo(() =>
    checks.map(c => `${c.entity}:${c.privilege}`).sort().join(','),
    [checks]
  );
  
  // Initialize state from cache
  const [permissions, setPermissions] = useState<Record<string, boolean>>(() => {
    if (!userId) {
      const result: Record<string, boolean> = {};
      checks.forEach(({ entity, privilege }) => {
        result[`${entity}:${privilege}`] = false;
      });
      return result;
    }

    const result: Record<string, boolean> = {};
    checks.forEach(({ entity, privilege }) => {
      const cacheKey = `perm:${userId}:${entity}:${privilege}`;
      const cached = getCachedPermission(cacheKey);
      result[`${entity}:${privilege}`] = cached !== null ? cached : false;
    });
    return result;
  });

  useEffect(() => {
    // If no user is authenticated, deny all permissions
    if (!userId) {
      const result: Record<string, boolean> = {};
      checks.forEach(({ entity, privilege }) => {
        result[`${entity}:${privilege}`] = false;
      });
      setPermissions(result);
      return;
    }

    // Check cache first for each permission
    const result: Record<string, boolean> = {};
    const uncachedChecks: Array<{ entity: Entity; privilege: Privilege }> = [];

    checks.forEach(({ entity, privilege }) => {
      const key = `${entity}:${privilege}`;

      // Try hydrated matrix first
      const matrixEntry = getMatrixEntry(userId, entity, privilege);
      if (matrixEntry) {
        result[key] = matrixEntry.allowed;
        const cacheKey = `perm:${userId}:${entity}:${privilege}`;
        setCachedPermission(cacheKey, matrixEntry.allowed);
        return;
      }

      const cacheKey = `perm:${userId}:${entity}:${privilege}`;
      const cached = getCachedPermission(cacheKey);

      if (cached !== null) {
        result[key] = cached;
      } else {
        uncachedChecks.push({ entity, privilege });
      }
    });

    // If all are cached, return immediately
    if (uncachedChecks.length === 0) {
      setPermissions(result);
      return;
    }

    // Fetch uncached permissions from API in bulk
    let cancelled = false;

    (async () => {
      // Hydrate matrix first to avoid bulk calls where possible
      await hydratePermissionMatrix(userId);
      if (cancelled) return;

      // Re-evaluate uncached after hydration
      const remainingChecks: Array<{ entity: Entity; privilege: Privilege }> = [];
      uncachedChecks.forEach(({ entity, privilege }) => {
        const key = `${entity}:${privilege}`;
        const matrixEntry = getMatrixEntry(userId, entity, privilege);
        if (matrixEntry) {
          result[key] = matrixEntry.allowed;
          const cacheKey = `perm:${userId}:${entity}:${privilege}`;
          setCachedPermission(cacheKey, matrixEntry.allowed);
        } else {
          remainingChecks.push({ entity, privilege });
        }
      });

      if (remainingChecks.length === 0) {
        setPermissions(result);
        return;
      }

      try {
        const token = getAccessToken();
        const response = await fetch('/api/v1/security/check-permissions/bulk', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            userId: userId,
            checks: remainingChecks.map(({ entity, privilege }) => ({
              entity,
              action: privilege
            }))
          })
        });

        if (cancelled) return;

        if (!response.ok) {
          console.warn(`[usePermissions] API error: ${response.status}`);
          // Set uncached to false as default
          uncachedChecks.forEach(({ entity, privilege }) => {
            result[`${entity}:${privilege}`] = false;
          });
          setPermissions(result);
          return;
        }

        const apiResult = await response.json();
        const bulkResults = apiResult?.data || {};

        if (cancelled) return;

        // Process bulk results and cache them
        remainingChecks.forEach(({ entity, privilege }) => {
          const key = `${entity}:${privilege}`;
          const permissionData = bulkResults[key];
          const allowed = permissionData?.allowed || false;

          result[key] = allowed;

          // Cache individual result
          const cacheKey = `perm:${userId}:${entity}:${privilege}`;
          setCachedPermission(cacheKey, allowed);
        });

        setPermissions(result);

      } catch (error) {
        if (cancelled) return;
        console.error('[usePermissions] Error checking permissions:', error);
        // Set uncached to false as default on error
        uncachedChecks.forEach(({ entity, privilege }) => {
          result[`${entity}:${privilege}`] = false;
        });
        setPermissions(result);
      }
    })();

    // Cleanup function to prevent state updates after unmount
    return () => {
      cancelled = true;
    };
  }, [userId, checksKey, checks]);

  return permissions;
}

/**
 * Hook to preload full permission matrix once per session
 * Reduces initial "no access" flashes by hydrating the matrix before rendering gated UI.
 */
export function usePreloadPermissions(): boolean {
  const user = getCachedUser();
  const userId = user?.id;
  const [ready, setReady] = useState<boolean>(() => {
    if (!userId) return false;
    const entry = getMatrixEntry(userId, 'users', 'READ');
    // If matrix exists (any entry), we consider it ready
    return Boolean(entry || loadMatrixFromStorage(userId));
  });

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setReady(false);
      return;
    }

    (async () => {
      await hydratePermissionMatrix(userId);
      if (!cancelled) {
        setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return ready;
}

/**
 * Hook to get current user's security roles
 *
 * @returns Array of security role assignments
 *
 * @example
 * ```tsx
 * function UserProfile() {
 *   const roles = useUserRoles();
 *
 *   return (
 *     <div>
 *       <h3>Your Roles:</h3>
 *       <ul>
 *         {roles.map(role => (
 *           <li key={role.id}>{role.roleName}</li>
 *         ))}
 *       </ul>
 *     </div>
 *   );
 * }
 * ```
 */
export function useUserRoles() {
  const user = getCachedUser();
  // Use stable references to prevent infinite re-renders
  const userId = user?.id;
  const roles = user?.roles;

  return useMemo(() => {
    if (!userId || !roles) {
      return [];
    }

    // Return user's role assignments from cached user data
    return roles;
  }, [userId, roles]);
}

/**
 * Hook to check if current user has ANY of the specified roles
 *
 * @param roleNames - Array of role names to check
 * @returns boolean - true if user has at least one of the roles
 *
 * @example
 * ```tsx
 * function AdminPanel() {
 *   const isAdmin = useHasRole(['System Administrator', 'Security Administrator']);
 *
 *   if (!isAdmin) {
 *     return <AccessDenied />;
 *   }
 *
 *   return <AdminDashboard />;
 * }
 * ```
 */
export function useHasRole(roleNames: string[]): boolean {
  const user = getCachedUser();
  // Use stable references to prevent infinite re-renders
  const userId = user?.id;
  const roles = user?.roles;
  // Stabilize roleNames array
  const roleNamesKey = useMemo(() => roleNames.sort().join(','), [roleNames]);

  return useMemo(() => {
    if (!userId || !roles) {
      return false;
    }

    // Check if user has any of the specified roles
    return roles.some((roleAssignment) =>
      roleNames.includes(roleAssignment.roleName || '')
    );
  }, [userId, roles, roleNamesKey, roleNames]);
}

/**
 * Hook to check if current user is authenticated
 *
 * @returns boolean - true if user is logged in
 */
export function useIsAuthenticated(): boolean {
  const user = getCachedUser();
  return user !== null;
}

/**
 * Utility function to convert AccessLevel enum to string
 */
export function accessLevelToString(level: AccessLevel): AccessLevelString {
  switch (level) {
    case AccessLevel.NONE:
      return 'NONE';
    case AccessLevel.USER:
      return 'USER';
    case AccessLevel.TEAM:
      return 'TEAM';
    case AccessLevel.BUSINESS_UNIT:
      return 'BUSINESS_UNIT';
    case AccessLevel.PARENT_CHILD_BU:
      return 'PARENT_CHILD_BU';
    case AccessLevel.ORGANIZATION:
      return 'ORGANIZATION';
    default:
      return 'NONE';
  }
}

/**
 * Utility function to convert AccessLevelString to enum
 */
export function stringToAccessLevel(level: AccessLevelString): AccessLevel {
  switch (level) {
    case 'NONE':
      return AccessLevel.NONE;
    case 'USER':
      return AccessLevel.USER;
    case 'TEAM':
      return AccessLevel.TEAM;
    case 'BUSINESS_UNIT':
      return AccessLevel.BUSINESS_UNIT;
    case 'PARENT_CHILD_BU':
      return AccessLevel.PARENT_CHILD_BU;
    case 'ORGANIZATION':
      return AccessLevel.ORGANIZATION;
    default:
      return AccessLevel.NONE;
  }
}
